import { useState } from 'react';
import { X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useWorkspaceCampaignPlacementJob } from '@/hooks/useWorkspaceCampaignPlacementJob';
import { WORKSPACE_CAMPAIGN_PLACEMENT_TERMINAL_STATUSES } from '@/api/workspaceCampaignPlacementTypes';
import type { CampaignAdProduct } from '@/types/spCampaigns';

export function TosBidIncreaseModal({
  profileId,
  campaignId,
  campaignName,
  adProduct,
  currentTosPercent,
  isOpen,
  onClose,
  onSuccess,
  onToast,
}: {
  profileId: string;
  campaignId: string;
  campaignName: string;
  adProduct: CampaignAdProduct;
  currentTosPercent: number | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onToast: (message: string, variant?: 'success' | 'error') => void;
}) {
  const placementJob = useWorkspaceCampaignPlacementJob(profileId);
  const [mode, setMode] = useState<'INCREASE' | 'SET'>('INCREASE');
  const [value, setValue] = useState('50');
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const submitting = placementJob.isRunning;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < 0) {
      setError('Enter a valid TOS bid percentage.');
      return;
    }
    if (mode === 'INCREASE' && numeric <= 0) {
      setError('Increase amount must be greater than 0.');
      return;
    }
    setError(null);
    try {
      const job = await placementJob.start({
        adjustments: [{ placement: 'TOP_OF_SEARCH', mode, value: numeric }],
        campaigns: [{ campaignId, adProduct }],
      });
      if (!job) throw new Error('Failed to start TOS bid update');
      if (job.status === 'failed') {
        throw new Error(job.applyProgress.error || 'TOS bid update failed');
      }
      if (
        WORKSPACE_CAMPAIGN_PLACEMENT_TERMINAL_STATUSES.has(job.status) &&
        (job.totals.failed ?? 0) > 0
      ) {
        throw new Error('TOS bid update failed for this campaign.');
      }
      onToast(`TOS bid updated for ${campaignName}.`, 'success');
      onSuccess();
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Failed to update TOS bid');
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-xl bg-surface p-6 shadow-xl">
        <div className="mb-4 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Raise TOS bid</h3>
          <button type="button" onClick={onClose} className="text-chrome-label hover:text-foreground-muted">
            <X className="h-5 w-5" />
          </button>
        </div>
        <p className="mb-1 text-sm text-foreground-muted">{campaignName}</p>
        <p className="mb-4 text-sm text-foreground-subtle">
          Current TOS bid:{' '}
          {currentTosPercent == null ? 'not set (0%)' : `${currentTosPercent.toFixed(0)}%`}
        </p>
        <form onSubmit={(event) => void submit(event)} className="space-y-3">
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground-muted">Adjustment</label>
            <select
              value={mode}
              onChange={(event) => setMode(event.target.value as 'INCREASE' | 'SET')}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm"
              disabled={submitting}
            >
              <option value="INCREASE">Increase by</option>
              <option value="SET">Set to</option>
            </select>
          </div>
          <div>
            <label className="mb-1 block text-sm font-medium text-foreground-muted">Percentage</label>
            <Input
              type="number"
              min="0"
              step="1"
              value={value}
              onChange={(event) => setValue(event.target.value)}
              disabled={submitting}
            />
          </div>
          {error ? <p className="text-sm text-error">{error}</p> : null}
          <div className="flex gap-2 pt-1">
            <Button type="button" variant="secondary" className="flex-1" onClick={onClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" className="flex-1" disabled={submitting}>
              {submitting ? 'Updating…' : 'Save TOS bid'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
